import { Box, Paper, Typography, useMediaQuery, useTheme } from '@mui/material'
import { Lock, TrendingUp, TrendingDown } from 'lucide-react'

// Shared building blocks for the Receipts (analytics) UI. Keeping the formatters, date helpers and
// presentational primitives here means AnalyticsTab, SponsorReportsSection and PublicReportPage all
// render identical bars/stats/papers and pull the accent from the theme (no hard-coded pink).

/* ---------- formatters ---------- */

export const pct = (x) => `${((x || 0) * 100).toFixed(1)}%`
export const num = (n) => (n || 0).toLocaleString()

export function countryFlag(code) {
    if (!code || code.length !== 2) return '🏳️'
    return code.toUpperCase().replace(/./g, (c) => String.fromCodePoint(127397 + c.charCodeAt(0)))
}

export function prettyLabel(s) {
    if (!s) return s
    return s.charAt(0) + s.slice(1).toLowerCase().replace(/_/g, ' ')
}

const MONTHS = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
]

function ordinal(n) {
    const suffixes = ['th', 'st', 'nd', 'rd']
    const v = n % 100
    return `${n}${suffixes[(v - 20) % 10] || suffixes[v] || suffixes[0]}`
}

/** Turn an ISO date (yyyy-MM-dd) into a friendly label, e.g. "2026-06-23" → "June 23rd". */
export function formatPretty(iso) {
    if (!iso) return ''
    const [y, m, d] = iso.split('-').map(Number)
    if (!y || !m || !d) return iso
    return `${MONTHS[m - 1]} ${ordinal(d)}`
}

/* ---------- date helpers (ISO yyyy-MM-dd) ---------- */

export function todayISO() {
    return new Date().toISOString().slice(0, 10)
}

export function daysAgoISO(n) {
    const d = new Date()
    d.setDate(d.getDate() - n)
    return d.toISOString().slice(0, 10)
}

/** A `from`/`to` window ending today and spanning `days` inclusive days. */
export function isoRange(days) {
    return { from: daysAgoISO(days - 1), to: todayISO() }
}

/* ---------- presentational primitives ---------- */

/**
 * The standard Vibio "paper" section — identical card + title styling to the Customization and
 * Settings tabs (Paper elevation 3, p:4, m:isMobile?0:2, rounded 30px; title is an h6, weight 600,
 * inherited Catamaran font). Optional `action` on the right; `locked` blurs the content and drops a
 * Pro lock overlay on top.
 */
export function SectionPaper({ title, action, children, locked, sx }) {
    const isMobile = useMediaQuery('(max-width:1050px)')
    return (
        <Paper elevation={3} sx={{ position: 'relative', p: 4, m: isMobile ? '0.75rem' : 2, borderRadius: '30px', ...sx }}>
            {(title || action) && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, mb: 1 }}>
                    {title
                        ? <Typography variant="h6" component="h6" sx={{ fontWeight: 600, fontFamily: 'inherit' }}>{title}</Typography>
                        : <span />}
                    {action}
                </Box>
            )}
            <Box sx={locked ? { filter: 'blur(5px)', pointerEvents: 'none', userSelect: 'none' } : undefined}>
                {children}
            </Box>
            {locked && <LockOverlay />}
        </Paper>
    )
}

export function LockOverlay() {
    const theme = useTheme()
    return (
        <Box sx={{
            position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', gap: 1,
            alignItems: 'center', justifyContent: 'center', textAlign: 'center', p: 2,
        }}>
            <Lock size={20} color={theme.palette.primary.main} />
            <Typography sx={{ fontWeight: 800, color: '#222' }}>A Pro feature</Typography>
            <Typography sx={{ fontSize: '.85rem', color: '#666' }}>Upgrade to unlock the full Receipts dashboard.</Typography>
        </Box>
    )
}

export function Stat({ value, label, valueSx }) {
    return (
        <Box>
            <Typography sx={{ fontWeight: 550, fontSize: { xs: '1.2rem', sm: '1.6rem' }, lineHeight: 1.1, color: '#111', ...valueSx }}>
                {value}
            </Typography>
            <Typography sx={{ fontSize: '.85rem', color: '#888' }}>{label}</Typography>
        </Box>
    )
}

export function BarRow({ label, share, leading }) {
    const theme = useTheme()
    return (
        <Box sx={{ mb: 1.4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, mb: 0.4 }}>
                <Typography noWrap sx={{ fontSize: '.92rem', color: '#1a1a1a', minWidth: 0 }}>
                    {leading && <span style={{ marginRight: 6 }}>{leading}</span>}{label}
                </Typography>
                <Typography sx={{ fontSize: '.92rem', fontWeight: 600, color: '#555' }}>{pct(share)}</Typography>
            </Box>
            <Box sx={{ height: 6, borderRadius: 4, background: '#eee', overflow: 'hidden' }}>
                <Box sx={{ height: '100%', width: `${Math.max((share || 0) * 100, 1)}%`, background: theme.palette.primary.main, borderRadius: 4 }} />
            </Box>
        </Box>
    )
}

export function Delta({ value, relative = false, small = false }) {
    if (value === null || value === undefined) return null
    const up = value >= 0
    const Icon = up ? TrendingUp : TrendingDown
    const text = `${Math.abs(value * 100).toFixed(1)}%`
    return (
        <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: small ? '.7rem' : '.78rem', fontWeight: 700,
            color: up ? '#0a7d33' : '#b00020', background: up ? '#e7f6ec' : '#fdecef',
            borderRadius: 8, padding: '1px 7px', marginLeft: 8,
        }}>
            <Icon size={small ? 12 : 13} /> {text}{relative ? ' vs prev' : ''}
        </span>
    )
}

export function Empty({ text }) {
    return <Typography sx={{ color: '#aaa', fontSize: '.9rem', py: 2 }}>{text}</Typography>
}

/** Outcome-framed upgrade nudge shown under a Free user's #1-only teaser. Plain muted text — no lock, no button. */
export function ProPrompt({ text }) {
    return <Typography sx={{ mt: 1.5, fontSize: '.85rem', color: '#888' }}>{text}</Typography>
}
